import { useEffect, useState, useMemo } from "react";
import { formatDate } from "@/lib/dateUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { downloadCsv, toCsv } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { Trash2, Edit2, Eye, Plus, FileText, Search, ClipboardList, Fuel } from "lucide-react";

interface ProductRow {
  product_id: string;
  product_name: string;
  qty: string;
  rate: string;
  cost: number;
  other_taxes: string;
  vat: string;
  tcs: string;
  incl_rate: number;
  total: number;
  density: string;
}

export default function LiquidPurchase() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'invoice' | 'product'>('invoice');

  // ── Invoice Header State ───────────────────────────────────────────────────
  const [invoiceDate, setInvoiceDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [invoiceNo, setInvoiceNo] = useState("");
  const [description, setDescription] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  // ── Product Input State (Product Details Tab) ──────────────────────────────
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState("");
  const [rate, setRate] = useState("");
  const [otherTaxes, setOtherTaxes] = useState("0");
  const [vat, setVat] = useState("0");
  const [tcs, setTcs] = useState("0");
  const [density, setDensity] = useState("0");

  const [itemsList, setItemsList] = useState<ProductRow[]>([]);
  const [roundingOff, setRoundingOff] = useState("0");

  // ── Filters State ──────────────────────────────────────────────────────────
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterText, setFilterText] = useState("");

  const [isSaving, setIsSaving] = useState(false);

  // ── Modal State for Viewing ────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [invoiceItems, setInvoiceItems] = useState<any[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const handleGoToProduct = () => {
    if (!invoiceNo.trim() || !vendorId) {
      toast({
        title: "Invoice No and Vendor required",
        description: "Please enter an invoice number and select a vendor on the Invoice tab first.",
        variant: "destructive",
      });
      return;
    }
    setActiveTab('product');
  };

  // ── Calculations for Current Input Row ─────────────────────────────────────
  const currentCost = useMemo(() => {
    const q = parseFloat(qty) || 0;
    const r = parseFloat(rate) || 0;
    return parseFloat((q * r).toFixed(3));
  }, [qty, rate]);

  const currentTotal = useMemo(() => {
    const oTax = parseFloat(otherTaxes) || 0;
    const vVal = parseFloat(vat) || 0;
    const tVal = parseFloat(tcs) || 0;
    return parseFloat((currentCost + oTax + vVal + tVal).toFixed(3));
  }, [currentCost, otherTaxes, vat, tcs]);

  const currentInclRate = useMemo(() => {
    const q = parseFloat(qty) || 0;
    if (q <= 0) return 0;
    return parseFloat((currentTotal / q).toFixed(4));
  }, [currentTotal, qty]);

  // ── Summary Calculations ───────────────────────────────────────────────────
  const activeCost = parseFloat(qty) && parseFloat(rate) ? (parseFloat(qty) * parseFloat(rate)) : 0;

  const totalCost = useMemo(() => {
    let base = itemsList.reduce((sum, item) => sum + item.cost, 0);
    if (productId && qty && rate) {
      base += activeCost;
    }
    return base;
  }, [itemsList, productId, qty, rate, activeCost]);

  const totalVat = useMemo(() => {
    let base = itemsList.reduce((sum, item) => sum + (parseFloat(item.vat) || 0), 0);
    if (productId && qty && rate) {
      base += parseFloat(vat) || 0;
    }
    return base;
  }, [itemsList, productId, qty, rate, vat]);

  const totalTcs = useMemo(() => {
    let base = itemsList.reduce((sum, item) => sum + (parseFloat(item.tcs) || 0), 0);
    if (productId && qty && rate) {
      base += parseFloat(tcs) || 0;
    }
    return base;
  }, [itemsList, productId, qty, rate, tcs]);

  const totalOtherTaxes = useMemo(() => {
    let base = itemsList.reduce((sum, item) => sum + (parseFloat(item.other_taxes) || 0), 0);
    if (productId && qty && rate) {
      base += parseFloat(otherTaxes) || 0;
    }
    return base;
  }, [itemsList, productId, qty, rate, otherTaxes]);
  
  const invoiceAmount = useMemo(() => {
    const base = totalCost + totalVat + totalTcs + totalOtherTaxes;
    const r = parseFloat(roundingOff) || 0;
    return parseFloat((base + r).toFixed(3));
  }, [totalCost, totalVat, totalTcs, totalOtherTaxes, roundingOff]);

  // ── Fetch Vendors ──────────────────────────────────────────────────────────
  const { data: vendors = [], isLoading: loadingVendors } = useQuery({
    queryKey: ["/api/vendors"],
    queryFn: async () => {
      const response = await fetch('/api/vendors');
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || 'Failed to fetch vendors');
      return result.rows || [];
    },
  });

  // ── Fetch Products ─────────────────────────────────────────────────────────
  const { data: products = [], isLoading: loadingProducts } = useQuery({
    queryKey: ["/api/fuel-products"],
    queryFn: async () => {
      const response = await fetch('/api/fuel-products');
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || 'Failed to fetch products');
      return result.rows || [];
    },
  });

  // ── Fetch Liquid Purchases ──────────────────────────────────────────────────
  const { data: rows = [], isLoading: loadingPurchases, refetch: refetchPurchases } = useQuery({
    queryKey: ["/api/liquid-purchases", filterFrom, filterTo, filterText],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterFrom) params.append('from', filterFrom);
      if (filterTo) params.append('to', filterTo);
      if (filterText) params.append('search', filterText);

      const response = await fetch(`/api/liquid-purchases?${params.toString()}`);
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || 'Failed to fetch liquid purchases');
      return result.rows || [];
    },
  });

  // ── Open Modal for Line Items ──────────────────────────────────────────────
  const openInvoiceDetails = async (invoice: any) => {
    setSelectedInvoice(invoice);
    setIsModalOpen(true);
    setLoadingItems(true);

    try {
      const response = await fetch(`/api/liquid-purchases/${invoice.id}/items`);
      const result = await response.json();
      if (result.ok) {
        setInvoiceItems(result.rows || []);
      } else {
        setInvoiceItems([]);
        toast({ title: "No items found for this invoice", variant: "destructive" });
      }
    } catch (e: any) {
      console.error('Failed to fetch invoice items', e);
      setInvoiceItems([]);
      toast({ title: "Failed to fetch invoice details", variant: "destructive" });
    } finally {
      setLoadingItems(false);
    }
  };

  const handleAddItem = () => {
    const prod = products.find((p: any) => String(p.id || p._id) === productId);
    const newRow: ProductRow = {
      product_id: productId || "",
      product_name: prod?.product_name || prod?.productName || "",
      qty: qty || "0",
      rate: rate || "0",
      cost: currentCost || 0,
      other_taxes: otherTaxes || "0",
      vat: vat || "0",
      tcs: tcs || "0",
      incl_rate: currentInclRate || 0,
      total: currentTotal || 0,
      density: density || "0",
    };

    setItemsList(prev => [...prev, newRow]);

    // Reset inputs
    setProductId("");
    setQty("");
    setRate("");
    setOtherTaxes("0");
    setVat("0");
    setTcs("0");
    setDensity("0");
    toast({ title: "Product row added" });
  };

  const handleRemoveItem = (idx: number) => {
    setItemsList(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUpdateItem = (index: number, key: keyof ProductRow, value: string) => {
    setItemsList(prev => prev.map((item, idx) => {
      if (idx !== index) return item;
      
      let updated = { ...item, [key]: value };
      if (key === 'product_id') {
        const prod = products.find((p: any) => String(p.id || p._id) === value);
        updated.product_name = prod?.product_name || prod?.productName || "Unknown";
      }

      // Recalculate cost, total, incl_rate for this row:
      const q = parseFloat(key === 'qty' ? value : updated.qty) || 0;
      const r = parseFloat(key === 'rate' ? value : updated.rate) || 0;
      const cost = parseFloat((q * r).toFixed(3));
      
      const oTax = parseFloat(key === 'other_taxes' ? value : updated.other_taxes) || 0;
      const vVal = parseFloat(key === 'vat' ? value : updated.vat) || 0;
      const tVal = parseFloat(key === 'tcs' ? value : updated.tcs) || 0;
      const total = parseFloat((cost + oTax + vVal + tVal).toFixed(3));
      
      const incl_rate = q > 0 ? parseFloat((total / q).toFixed(4)) : 0;
      
      return {
        ...updated,
        cost,
        total,
        incl_rate
      };
    }));
  };

  const handleDuplicateItem = (item: ProductRow) => {
    setItemsList(prev => [...prev, { ...item }]);
    toast({ title: "Item duplicated" });
  };

  // ── Save Invoice ───────────────────────────────────────────────────────────
  const save = async () => {
    if (!invoiceNo) { toast({ title: "Invoice No is required", variant: "destructive" }); return; }
    if (!vendorId) { toast({ title: "Vendor is required", variant: "destructive" }); return; }

    // Dynamically build list of items including any unsaved active input row
    let finalItems = [...itemsList];
    if (productId && qty && rate) {
      const prod = products.find((p: any) => String(p.id || p._id) === productId);
      finalItems.push({
        product_id: productId,
        product_name: prod?.product_name || prod?.productName || "",
        qty,
        rate,
        cost: currentCost,
        other_taxes: otherTaxes,
        vat,
        tcs,
        incl_rate: currentInclRate,
        total: currentTotal,
        density
      });
    }

    if (finalItems.length === 0) {
      toast({ title: "Please add at least one Product in Product Details tab", variant: "destructive" });
      return;
    }

    try {
      setIsSaving(true);
      // Form payload
      const payload = {
        date: invoiceDate, // main table date
        invoice_date: invoiceDate,
        invoice_no: invoiceNo,
        amount: invoiceAmount, // dynamic invoice total
        description,
        vendor_id: vendorId,
        vendor_name: vendors.find((v: any) => v.id === vendorId)?.vendor_name || "",
        items: finalItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          purchase_rate: item.rate,
          quantity: item.qty,
          cost: item.cost,
          vat: item.vat,
          other_taxes: item.other_taxes,
          tcs: item.tcs,
          item_total: item.total,
          inv_density: item.density,
          measured_density: item.density,
          vari_density: 0
        })),
        total_cost: totalCost,
        total_vat: totalVat,
        total_tcs: totalTcs,
        total_other_taxes: totalOtherTaxes,
        rounding_off: Number(roundingOff) || 0
      };

      const url = editingId ? `/api/liquid-purchases/${editingId}` : '/api/liquid-purchases';
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (!result.ok) throw new Error(result.error || 'Failed to save liquid purchase');

      toast({ title: "Purchase invoice saved successfully" });
      
      // Reset form states
      setInvoiceNo("");
      setDescription("");
      setVendorId("");
      setItemsList([]);
      setRoundingOff("0");
      setEditingId(null);
      setActiveTab('invoice');
      refetchPurchases();
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message || String(e), variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = async (r: any) => {
    setEditingId(r.id);
    setInvoiceDate(r.invoice_date || r.date);
    setInvoiceNo(r.invoice_no);
    setDescription(r.description || "");
    setVendorId(r.vendor_id);
    setRoundingOff(String(r.rounding_off || 0));

    // Fetch line items for this edit
    try {
      const response = await fetch(`/api/liquid-purchases/${r.id}/items`);
      const result = await response.json();
      if (result.ok && result.rows) {
        const mappedItems: ProductRow[] = result.rows.map((item: any) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          qty: String(item.quantity),
          rate: String(item.purchase_rate),
          cost: Number(item.cost),
          other_taxes: String(item.other_taxes || 0),
          vat: String(item.vat || 0),
          tcs: String(item.tcs || 0),
          incl_rate: Number(item.item_total) / Number(item.quantity || 1),
          total: Number(item.item_total),
          density: String(item.inv_density || 0),
        }));
        setItemsList(mappedItems);
      } else {
        setItemsList([]);
      }
    } catch (e) {
      console.error("Failed to load edit items", e);
      setItemsList([]);
    }

    setActiveTab('invoice');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (selectedInvoice) {
    return (
      <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
        {/* Breadcrumbs with Back button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
            <span className="hover:underline cursor-pointer" onClick={() => setSelectedInvoice(null)}>Dashboard</span>
            <span>&gt;</span>
            <span className="hover:underline cursor-pointer" onClick={() => setSelectedInvoice(null)}>Invoice Details</span>
            <span>&gt;</span>
            <span className="text-slate-800 font-semibold">Invoice product Details</span>
          </div>
          <Button
            onClick={() => setSelectedInvoice(null)}
            className="bg-[#0e9f6e] hover:bg-[#0b8a5c] text-white font-bold px-4 h-9 rounded-lg"
          >
            &larr; Back to Invoices
          </Button>
        </div>

        {/* Detailed view card */}
        <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden">
          <CardContent className="p-6 space-y-6">
            {/* Header block with gray-slate background */}
            <div className="bg-[#4a606d] p-4 rounded-lg flex flex-wrap items-center justify-between gap-4 text-white text-xs font-bold shadow-inner">
              <div className="flex items-center gap-2">
                <span>Invoice Number :</span>
                <input
                  type="text"
                  readOnly
                  className="bg-white text-slate-800 px-3 py-1.5 rounded font-bold border-none w-44 text-center focus:outline-none"
                  value={selectedInvoice.invoice_no}
                />
              </div>
              <div className="flex items-center gap-2">
                <span>Date :</span>
                <input
                  type="text"
                  readOnly
                  className="bg-white text-slate-800 px-3 py-1.5 rounded font-bold border-none w-44 text-center focus:outline-none"
                  value={selectedInvoice.date ? new Date(selectedInvoice.date).toLocaleDateString('en-GB') : '-'}
                />
              </div>
              <div className="flex items-center gap-2">
                <span>Amount :</span>
                <input
                  type="text"
                  readOnly
                  className="bg-white text-slate-800 px-3 py-1.5 rounded font-extrabold border-none w-44 text-center focus:outline-none"
                  value={(Number(selectedInvoice.amount) || Number(selectedInvoice.total_amount) || 0).toFixed(3)}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter:</span>
                <Input placeholder="Type to filter..." className="w-52 h-9 text-xs" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500">Show:</span>
                <select className="h-9 rounded-md border border-slate-200 px-2 text-xs bg-white font-bold"><option>All</option></select>
                <Button variant="outline" size="sm" className="border-green-500 text-green-600 hover:bg-green-50 font-bold text-xs">CSV</Button>
                <Button variant="outline" size="sm" className="border-red-500 text-red-600 hover:bg-red-50 font-bold text-xs">PDF</Button>
              </div>
            </div>

            {/* Line Items Table */}
            {loadingItems ? (
              <div className="text-center py-12 text-slate-400">
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
                  Loading line items...
                </span>
              </div>
            ) : invoiceItems.length === 0 ? (
              <div className="text-center py-12 text-slate-400">No items found for this invoice</div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <Table className="text-xs">
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-bold text-slate-600 h-10">S.No</TableHead>
                        <TableHead className="font-bold text-slate-600 h-10">Product Name</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Purchase Rate</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Quantity</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Cost</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">VAT</TableHead>
                        <TableHead className="font-bold text-slate-600 text-center h-10">Other Taxs</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">TCS</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Item Total</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Inv.Density</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Measured Density</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Vari Density</TableHead>
                        <TableHead className="font-bold text-slate-600 text-center h-10">Other</TableHead>
                        <TableHead className="font-bold text-slate-600 text-center h-10">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoiceItems.map((item: any, idx: number) => (
                        <TableRow key={item.id || idx} className="hover:bg-slate-50/50">
                          <TableCell className="font-medium text-slate-400">{idx + 1}</TableCell>
                          <TableCell className="font-semibold text-slate-800">{item.product_name || "Unknown"}</TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {item.purchase_rate ? parseFloat(item.purchase_rate).toFixed(3) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {item.quantity ? parseFloat(item.quantity).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {item.cost ? parseFloat(item.cost).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">{item.vat ? parseFloat(item.vat).toFixed(3) : '0.00'}</TableCell>
                          <TableCell className="text-center font-medium tabular-nums">
                            <div>
                              <div className="font-semibold">{item.other_taxes ? parseFloat(item.other_taxes).toFixed(3) : '0.00'}</div>
                              <div className="text-[10px] text-slate-400">
                                Cess : {item.cess ? parseFloat(item.cess).toFixed(3) : '0.00'}
                              </div>
                              <div className="text-[10px] text-slate-400">
                                Add.Vat : {item.add_vat ? parseFloat(item.add_vat).toFixed(3) : '0.00'}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">{item.tcs ? parseFloat(item.tcs).toFixed(3) : '0.00'}</TableCell>
                          <TableCell className="text-right font-bold text-slate-800 tabular-nums">
                            {item.item_total ? parseFloat(item.item_total).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">{item.inv_density ? parseFloat(item.inv_density).toFixed(3) : '0.00'}</TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {item.measured_density ? parseFloat(item.measured_density).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">{item.vari_density ? parseFloat(item.vari_density).toFixed(3) : '0.00'}</TableCell>
                          <TableCell className="text-center font-medium">
                            <div className="space-y-0.5">
                              <div className="text-[10px] text-slate-500">LFR : {item.lfr ? parseFloat(item.lfr).toFixed(3) : '0.00'}</div>
                              <div className="text-[10px] text-slate-500">DC : {item.dc ? parseFloat(item.dc).toFixed(3) : '0.00'}</div>
                              <a href="#" onClick={(e) => e.preventDefault()} className="text-[10px] text-blue-600 hover:underline block font-semibold">Label</a>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <button
                              onClick={() => {
                                const target = selectedInvoice;
                                setSelectedInvoice(null);
                                handleEdit(target);
                              }}
                              className="text-blue-600 hover:text-blue-800 hover:underline font-bold text-xs"
                            >
                              Label
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Pagination Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100 text-xs text-slate-500 font-medium">
                  <div>
                    Showing 1 to {invoiceItems.length} of {invoiceItems.length} entries
                  </div>
                  <div className="flex items-center gap-1">
                    <button className="px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50" disabled>&larr;</button>
                    <button className="px-3 py-1 bg-[#4a606d] text-white border border-[#4a606d] rounded">1</button>
                    <button className="px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50" disabled>&rarr;</button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm text-slate-500 font-medium">
        <span className="hover:text-blue-600 cursor-pointer">Dashboard</span>
        <span>/</span>
        <span className="text-slate-800 font-semibold">Liquid Invoice Details</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('invoice')}
          className={`px-6 py-2.5 rounded-t-lg font-bold text-sm transition-colors shadow-sm ${
            activeTab === 'invoice'
              ? 'bg-[#0e9f6e] text-white'
              : 'bg-white text-slate-700 hover:bg-slate-100 border-t border-x'
          }`}
        >
          Invoice
        </button>
        <button
          onClick={handleGoToProduct}
          className={`px-6 py-2.5 rounded-t-lg font-bold text-sm transition-colors shadow-sm ${
            activeTab === 'product'
              ? 'bg-[#0e9f6e] text-white'
              : 'bg-white text-slate-700 hover:bg-slate-100 border-t border-x'
          }`}
        >
          Product Details
        </button>
      </div>

      {/* Blue Form Card */}
      <Card className="bg-gradient-to-br from-blue-700 to-indigo-900 text-white rounded-b-xl rounded-tr-xl border-none shadow-lg overflow-hidden -mt-[1px]">
        <CardContent className="p-6 space-y-6">
          {activeTab === 'invoice' ? (
            // ── Tab 1: Invoice Header Form ────────────────────────────────────
            <div className="space-y-5">
              {/* Choose Date Input */}
              <div className="flex items-center gap-4">
                <div className="h-10 min-w-[12rem] px-4 flex items-center font-bold rounded-lg bg-[#fbbf24] text-slate-900 shadow-inner">
                  Choose Date
                </div>
                <Input
                  type="date"
                  className="bg-white text-slate-900 border-none w-52 focus-visible:ring-2 focus-visible:ring-amber-400 font-medium"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>

              {/* Form Input fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="space-y-1">
                  <div className="text-xs font-bold text-slate-200 uppercase tracking-wider">Invoice No *</div>
                  <Input
                    className="bg-white text-slate-900 border-none placeholder:text-slate-400 font-medium"
                    placeholder="Invoice No *"
                    value={invoiceNo}
                    onChange={(e) => setInvoiceNo(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-xs font-bold text-slate-200 uppercase tracking-wider">Upload Image</div>
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif"
                    onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                    className="bg-white text-slate-900 border-none cursor-pointer file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                  />
                  <div className="text-[10px] text-blue-200 mt-0.5">Allowed (JPEG, JPG, GIF, PNG) Maxsize: 2MB</div>
                </div>

                <div className="space-y-1 col-span-1 md:col-span-2 lg:col-span-1">
                  <div className="text-xs font-bold text-slate-200 uppercase tracking-wider">Description</div>
                  <Input
                    className="bg-white text-slate-900 border-none placeholder:text-slate-400 font-medium"
                    placeholder="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-xs font-bold text-slate-200 uppercase tracking-wider">Vendor *</div>
                  <Select value={vendorId} onValueChange={setVendorId}>
                    <SelectTrigger className="bg-white text-slate-900 border-none font-medium">
                      <SelectValue placeholder="Vendor *" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingVendors ? (
                        <SelectItem value="loading" disabled>Loading vendors...</SelectItem>
                      ) : vendors && vendors.length > 0 ? (
                        vendors.map((v: any) => (<SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>))
                      ) : (
                        <SelectItem value="no-vendors" disabled>No vendors available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="text-right pt-2">
                <Button 
                  onClick={handleGoToProduct}
                  className="bg-teal-500 hover:bg-teal-600 text-white font-bold px-6 shadow-md rounded-lg"
                >
                  Next: Product Details &rarr;
                </Button>
              </div>
            </div>
          ) : (
            // ── Tab 2: Product Details Form ───────────────────────────────────
            <div className="space-y-6">
              <h3 className="text-sm font-bold uppercase tracking-wider border-b border-white/20 pb-2 flex items-center gap-2">
                <Fuel className="h-4 w-4 text-amber-400" /> Add Product Items
              </h3>

              {/* Row 1: Line Item Inputs */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-4 items-end">
                <div className="space-y-1 xl:col-span-2">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Product *</div>
                  <Select value={productId} onValueChange={setProductId}>
                    <SelectTrigger className="bg-white text-slate-900 border-none font-medium h-9 text-xs">
                      <SelectValue placeholder="Choose Product *" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingProducts ? (
                        <SelectItem value="loading" disabled>Loading...</SelectItem>
                      ) : products && products.length > 0 ? (
                        products.map((p: any) => (
                          <SelectItem key={p.id || p._id} value={p.id || p._id}>
                            {p.product_name || p.productName}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-products" disabled>No products</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Qty (Ltr) *</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="Qty"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Rate *</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="Rate"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Cost *</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                    placeholder="0"
                    value={currentCost || ""}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Other Taxes *</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="Taxes"
                    value={otherTaxes}
                    onChange={(e) => setOtherTaxes(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Vat *</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="VAT"
                    value={vat}
                    onChange={(e) => setVat(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Incl. Rate</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                    placeholder="0.0000"
                    value={currentInclRate || ""}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">TCS *</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="TCS"
                    value={tcs}
                    onChange={(e) => setTcs(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Total *</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                    placeholder="0"
                    value={currentTotal || ""}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Density</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="Density"
                    value={density}
                    onChange={(e) => setDensity(e.target.value)}
                  />
                </div>

                <div className="flex justify-center xl:col-span-1">
                  <Button
                    type="button"
                    onClick={handleAddItem}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-9 w-9 p-0 flex items-center justify-center shadow"
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Items List (Editable input rows inside Card) */}
              {itemsList.map((item, idx) => (
                <div key={idx} className="border border-white/20 rounded-xl p-4 bg-white/5 space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-4 items-end">
                    <div className="space-y-1 xl:col-span-2">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Product *</div>
                      <Select
                        value={item.product_id}
                        onValueChange={(val) => handleUpdateItem(idx, 'product_id', val)}
                      >
                        <SelectTrigger className="bg-white text-slate-900 border-none font-medium h-9 text-xs">
                          <SelectValue placeholder="Product *" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((p: any) => (
                            <SelectItem key={p.id || p._id} value={p.id || p._id}>
                              {p.product_name || p.productName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase font-bold">Qty (Ltr)</div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.qty}
                        onChange={(e) => handleUpdateItem(idx, 'qty', e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Rate *</div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.rate}
                        onChange={(e) => handleUpdateItem(idx, 'rate', e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Cost *</div>
                      <Input
                        type="number"
                        readOnly
                        className="bg-slate-100/10 text-slate-200 border border-white/20 h-9 text-xs font-bold"
                        value={item.cost || ""}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Other Taxes *</div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.other_taxes}
                        onChange={(e) => handleUpdateItem(idx, 'other_taxes', e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Vat *</div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.vat}
                        onChange={(e) => handleUpdateItem(idx, 'vat', e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Incl. Rate</div>
                      <Input
                        type="number"
                        readOnly
                        className="bg-slate-100/10 text-slate-200 border border-white/20 h-9 text-xs font-bold"
                        value={item.incl_rate ? item.incl_rate.toFixed(4) : ""}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">TCS *</div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.tcs}
                        onChange={(e) => handleUpdateItem(idx, 'tcs', e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Total *</div>
                      <Input
                        type="number"
                        readOnly
                        className="bg-slate-100/10 text-slate-200 border border-white/20 h-9 text-xs font-bold"
                        value={item.total || ""}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase font-semibold flex items-center gap-1">
                        Density
                      </div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.density}
                        onChange={(e) => handleUpdateItem(idx, 'density', e.target.value)}
                      />
                    </div>

                    <div className="flex justify-center gap-1 xl:col-span-1">
                      <Button
                        type="button"
                        onClick={() => handleDuplicateItem(item)}
                        className="bg-blue-600/30 hover:bg-blue-600 border border-blue-400 text-white rounded-lg h-9 w-9 p-0 flex items-center justify-center shadow"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        onClick={() => handleRemoveItem(idx)}
                        className="bg-red-600/30 hover:bg-red-600 border border-red-400 text-white rounded-lg h-9 w-9 p-0 flex items-center justify-center shadow"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Row 2: Invoice Totals & Summary Fields */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 border-t border-white/20 pt-4">
                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Total Cost</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none font-bold"
                    value={totalCost.toFixed(2)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Total VAT</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none font-bold"
                    value={totalVat.toFixed(2)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Total TCS</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none font-bold"
                    value={totalTcs.toFixed(2)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Total Other Taxes</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none font-bold"
                    value={totalOtherTaxes.toFixed(2)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Rounding Off</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none font-bold"
                    value={roundingOff}
                    onChange={(e) => setRoundingOff(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-amber-300 uppercase">Invoice Amount *</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-amber-100 text-slate-800 border-none font-extrabold"
                    value={invoiceAmount.toFixed(2)}
                  />
                </div>
              </div>

              {/* Payment Checkbox */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="payment-checkbox"
                  className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 w-4 h-4 cursor-pointer"
                />
                <label htmlFor="payment-checkbox" className="text-sm font-bold select-none cursor-pointer">
                  Payment
                </label>
              </div>

              {/* Bottom Action buttons */}
              <div className="flex justify-center gap-4 pt-4 border-t border-white/20">
                <Button
                  onClick={save}
                  disabled={isSaving}
                  className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold px-12 transition-transform active:scale-95 shadow"
                >
                  {isSaving ? "SAVING..." : editingId ? "UPDATE INVOICE" : "SAVE INVOICE"}
                </Button>
                {editingId && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingId(null);
                      setInvoiceNo("");
                      setDescription("");
                      setVendorId("");
                      setItemsList([]);
                      setRoundingOff("0");
                      setActiveTab('invoice');
                    }}
                    className="rounded-full border-white/30 text-white hover:bg-white/10"
                  >
                    Cancel Edit
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Table Filter and Search Section ───────────────────────────────────── */}
      <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 flex-grow sm:flex-none">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">From Date</span>
              <Input
                type="date"
                className="h-9 w-40 text-xs font-medium bg-white border border-slate-200 rounded-md"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 flex-grow sm:flex-none">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">To Date</span>
              <Input
                type="date"
                className="h-9 w-40 text-xs font-medium bg-white border border-slate-200 rounded-md"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 flex-grow sm:flex-none">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Filter Date</span>
              <Input
                type="date"
                className="h-9 w-40 text-xs font-medium bg-white border border-slate-200 rounded-md"
              />
            </div>
            <div className="flex items-center gap-2 flex-grow">
              <div className="flex items-center gap-1.5 border border-slate-200 rounded-md px-2.5 py-1.5 bg-slate-50 flex-grow h-9">
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Type to filter..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="text-xs bg-transparent outline-none w-full text-slate-700 placeholder:text-slate-400 font-medium"
                />
              </div>
              <Button
                onClick={() => refetchPurchases()}
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold h-9 shadow-sm"
              >
                Search
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Purchase Invoice List Table ────────────────────────────────────────── */}
      <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold">
              <span>Show:</span>
              <select className="h-9 rounded-md border border-slate-200 px-2.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 font-bold">
                <option>All</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="border-green-500 text-green-600 hover:bg-green-50 font-bold text-xs"
                onClick={() => {
                  if (!rows || rows.length === 0) {
                    toast({ title: "No data to export", variant: "destructive" });
                    return;
                  }
                  const rowsCsv = rows.map((r: any, idx: number) => ({
                    SlNo: idx + 1,
                    Date: r.date,
                    InvoiceNo: r.invoice_no,
                    Amount: Number(r.amount) || Number(r.total_amount) || 0,
                    Vendor: r.vendor_name || '',
                    Description: r.description || '',
                  }));
                  downloadCsv('liquid-purchases.csv', toCsv(rowsCsv));
                }}
              >
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-red-500 text-red-600 hover:bg-red-50 font-bold text-xs"
              >
                PDF
              </Button>
              <div className="flex items-center gap-2 ml-4">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter:</span>
                <Input placeholder="Type to filter..." className="w-52 h-9 text-xs" />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-bold text-slate-600 h-10">S.No</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">Date</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">Invoice No</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">Image</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">Vendor</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">Description</TableHead>
                  <TableHead className="font-bold text-slate-600 text-right h-10">Amount</TableHead>
                  <TableHead className="font-bold text-slate-600 text-center h-10">View</TableHead>
                  <TableHead className="font-bold text-slate-600 text-center h-10">Action</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">User Log Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingPurchases ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-slate-400 py-12">
                      <span className="flex items-center justify-center gap-2">
                        <span className="h-4 w-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
                        Loading purchases...
                      </span>
                    </TableCell>
                  </TableRow>
                ) : !rows || rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-slate-400 py-12">
                      No liquid purchases found
                    </TableCell>
                  </TableRow>
                ) : rows.map((r: any, idx: number) => (
                  <TableRow key={r.id || idx} className="hover:bg-slate-50 border-b border-slate-100">
                    <TableCell className="font-medium text-slate-500">{idx + 1}</TableCell>
                    <TableCell className="font-medium text-slate-700">{r.date ? new Date(r.date).toLocaleDateString('en-GB') : '-'}</TableCell>
                    <TableCell className="font-bold text-slate-800">{r.invoice_no}</TableCell>
                    <TableCell className="text-center">
                      {r.image_url ? (
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold text-[10px]">Yes</span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="font-semibold text-slate-800">{r.vendor_name || '-'}</TableCell>
                    <TableCell className="text-slate-600 font-medium">{r.description || '-'}</TableCell>
                    <TableCell className="text-right font-bold text-slate-800 tabular-nums">
                      ₹{(Number(r.amount) || Number(r.total_amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openInvoiceDetails(r)}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded-lg"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex gap-1.5 justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(r)}
                          className="border-slate-200 text-blue-600 hover:bg-blue-50 p-1.5 h-8 w-8 rounded-lg"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            if (!confirm('Delete this purchase invoice?')) return;
                            try {
                              const response = await fetch(`/api/liquid-purchases/${r.id}`, { method: 'DELETE', credentials: 'include' });
                              const result = await response.json();
                              if (result.ok) {
                                toast({ title: "Success", description: "Purchase invoice deleted" });
                                refetchPurchases();
                              } else {
                                throw new Error(result.error || 'Failed to delete');
                              }
                            } catch (error: any) {
                              toast({ variant: "destructive", title: "Error", description: error.message || 'Failed to delete' });
                            }
                          }}
                          className="border-slate-200 text-rose-600 hover:bg-rose-50 p-1.5 h-8 w-8 rounded-lg"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-[10px] text-slate-400 font-medium whitespace-nowrap">
                      {r.created_by ? `Created: ${r.created_by}` : ''} {r.created_at ? new Date(r.created_at).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


